import { Dialog } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { Search } from "modules/common/components/form/Search";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { useMst } from "modules/root/models/hooks";
import { can } from "modules/session/auth/access";
import { OneCImportDialog, POSITION_ROLES } from "modules/spending/onec/components/OneCImportDialog";
import func from "modules/spending/functionalities";
import React, { useEffect, useState } from "react";
import { PositionDialog } from "./details/PositionDialog";
import { EmployeePositionsList } from "./list/EmployeePositionsList";
import styles from "./PositionsDictionaryDialog.module.scss";

interface PositionsDictionaryDialogProps {
    onClose(): void;
    /** создали новую должность — например, чтобы сразу выбрать её в карточке сотрудника */
    onCreated?(id: string): void;
}

/**
 * Справочник должностей в окне: таблица, «Добавить должность», «Импорт из 1С» и форма
 * «Должность» поверх — без перехода на другую страницу (открывается из дерева структуры
 * и из карточки сотрудника). Должность — «кем работает», к подразделениям не привязана.
 */
export const PositionsDictionaryDialog = observer(({ onClose, onCreated }: PositionsDictionaryDialogProps) => {
    const { employeePositions: store, session, onec } = useMst();
    const [open, setOpen] = useState<string | null>(null);
    const [showImport, setShowImport] = useState(false);
    const canChange = can(func.DEPARTMENT_EDIT, session.access);
    const canImport = can(func.ONEC_IMPORT, session.access);

    useEffect(() => {
        store.list.load();
    }, [store]);

    return (
        <Dialog
            isOpen
            title="Должности"
            onClose={onClose}
            className={`figma-dialog ${styles.dialog}`}
            backdropClassName="standard"
            canOutsideClickClose={false}
            enforceFocus={false}
        >
            <div className={styles.body}>
                <div className={styles.toolbar}>
                    {canChange && (
                        <PlanrButton
                            type="secondary"
                            size="small"
                            icon="general-plus-big"
                            style={{ borderRadius: 120 }}
                            onClick={() => setOpen("")}
                        >
                            Добавить должность
                        </PlanrButton>
                    )}
                    {canImport && (
                        <PlanrButton
                            type="graybtn"
                            size="small"
                            icon="general-import"
                            style={{ borderRadius: 120 }}
                            onClick={() => setShowImport(true)}
                        >
                            Импорт из 1С
                        </PlanrButton>
                    )}
                    <span className={styles.spacer} />
                    <Search query={store.list.query} onSearch={store.list.setQuery} style={{ marginLeft: 0 }} />
                </div>
                <div className={styles.grid}>
                    <EmployeePositionsList
                        store={store.list}
                        onSelected={(id) => setOpen(id)}
                        onRemoved={() => store.list.load()}
                        baseUrl={session.baseUrl}
                        canRemove={canChange}
                    />
                </div>
            </div>

            {open !== null && (
                <PositionDialog
                    positionId={open}
                    onClose={() => setOpen(null)}
                    onSaved={(id) => {
                        const isNew = open === "";
                        setOpen(null);
                        store.list.load();
                        if (isNew && id) {
                            onCreated?.(id);
                        }
                    }}
                />
            )}

            {canImport && (
                <OneCImportDialog
                    isOpen={showImport}
                    onClose={() => setShowImport(false)}
                    store={onec}
                    entityKey="positions"
                    title="Импорт должностей из 1С"
                    roles={POSITION_ROLES}
                    onImported={() => store.list.load()}
                />
            )}
        </Dialog>
    );
});
